import KeyValuePair from '@/modules/core/support/KeyValuePair'
import { User as AuthorizedUser } from '@/modules/auth/types'
import UserGroup from '@/modules/user/model/UserGroup'

export type UserAttributeValidValue = string | number | boolean

/**
 * Usuario registrado en la aplicación
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class User {
    public id = 0
    public name = ''
    public _email = ''
    public nickname = ''
    public registrationDate = new Date()
    public attributes: {[key: string]: UserAttributeValidValue} = {}
    public postalCode = 0
    public verified = false
    public updatedEmail = ''
    public groups: UserGroup[] = []

    /**
     * Si el usuario aún no ha sido insertado en el servidor la entidad puede definir
     * la contraseña que será establecida
     */
    public newPassword = ''

    public get email () {
        return this._email
    }

    public set email (email: string) {
        this._email = email
        this.updatedEmail = email
    }

    public get isNew (): boolean {
        return !this.id
    }

    /**
     * Añade un atributo de usuario
     *
     * @param key
     * @param value
     */
    public addAttribute (key: string, value: UserAttributeValidValue) {
        this.attributes[key] = value

        return this
    }

    public static deserialize (data: KeyValuePair) {
        const user = new this()
        user.id = data.id
        user.name = data.name
        user.email = data.email
        user.nickname = data.nickname || ''
        user.registrationDate = new Date(data.registration_date)
        user.attributes = data.attributes || {}
        user.postalCode = data.postal_code || 0
        user.verified = data.verified || false
        if (Array.isArray(data.groups)) {
            user.groups = data.groups.map((g: KeyValuePair) => UserGroup.deserialize(g))
        }

        return user
    }

    public serialize () {
        const data: KeyValuePair = {
            name: this.name,
            email: this.updatedEmail,
            nickname: this.nickname,
            postal_code: this.postalCode,
            attributes: this.attributes,
            verified: this.verified,
            groups: this.groups.map(g => g.id),
        }

        if (this.isNew) {
            data['password'] = this.newPassword
        }

        return data
    }

    /**
     * Crea una entidad de usuario a partir de un usuario del sistema de autorización
     *
     * @param user
     */
    public static fromAuthorized (user: AuthorizedUser) {
        const u = new this()
        u.id = user.id
        u.email = user.email
        u.name = user.name

        return u
    }

    /**
     * Compara una entidad usuario con otra para comprobar si sus datos son idénticos
     *
     * @param user
     */
    public compare (user: User) {
        const sumID = (groups: UserGroup[]) => groups.map(g => g.id).reduce((a, v) => a + v)

        return this.id === user.id
            && this.name === user.name
            && this.email === user.email
            && this.nickname === user.nickname
            && this.registrationDate.getTime() === user.registrationDate.getTime()
            && JSON.stringify(this.attributes) === JSON.stringify(user.attributes)
            && this.postalCode === user.postalCode
            && this.verified === user.verified
            && sumID(this.groups) === sumID(user.groups)
    }
}
